import { useStore } from '@/hooks/useStore';
import { observer } from 'mobx-react-lite';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogPortal,
    DialogTitle
} from '../ui/DialogUI';
import { DialogClose } from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cva } from 'class-variance-authority';
import { cn } from '@/utils/utils';
import ModalFooter from './ModalFooter';
import SuccessStatusImg from '@/assets/icons/success-noti-icon.png';
import ErrorStatusImg from '@/assets/icons/error-noti-icon.png';
import { Trans } from 'react-i18next';
import { ROUTES } from '@/configs/constants';
import { useLocation } from 'react-router-dom';
import { isPathEk } from '@/utils/routes';

export const modalVariants = cva('', {
    variants: {
        type: {
            default: '',
            success: 'text-[#07bc0c]',
            error: 'text-[#e74c3c]',
            info: 'text-[#3498db]',
            warn: 'text-[#f1c40f]'
        },
        size: {
            'default': '[--modal-max-width:32rem]', // max-w-lg = 512px
            'alert': '[--modal-max-width:32rem]',
            'xs': '[--modal-max-width:20rem]', // max-w-xs = 320px
            'sm': '[--modal-max-width:24rem]', // 384px
            'md': '[--modal-max-width:28rem]', // 448px
            'lg': '[--modal-max-width:32rem]',
            'xl': '[--modal-max-width:36rem]',
            '2xl': '[--modal-max-width:42rem]',
            '3xl': '[--modal-max-width:48rem]',
            '4xl': '[--modal-max-width:56rem]',
            '5xl': '[--modal-max-width:64rem]',
            '6xl': '[--modal-max-width:72rem]',
            '7xl': '[--modal-max-width:80rem]'
        }
    },
    defaultVariants: {
        type: 'default',
        size: 'default'
    }
});

export default observer(function Modals() {
    // hook
    const { pathname } = useLocation();
    const isEk = isPathEk(pathname);

    // store
    const {
        modalStore: { instances, hideModal }
    } = useStore();

    const checkTypeModal = (type: string | undefined | null): boolean => {
        if (!type) return false;
        if (type === 'error' || type === 'success' || type === 'info' || type === 'warn') return true;
        return false;
    };

    const isErrorModal = (type: string | undefined | null): boolean => {
        if (!type) return false;
        if (type === 'error') return true;
        return false;
    };

    const getImgType = (type: string | undefined | null): string => {
        if (!type) return '';
        switch (type) {
            case 'success':
                return `${SuccessStatusImg}`;
            case 'error':
                return `${ErrorStatusImg}`;
            default:
                return '';
        }
    };

    const checkRecordExist = (i18nKey: string) => {
        return i18nKey?.includes('ERROR_LOCATION_DOES_NOT_EXIST') || i18nKey?.includes('ERROR_BIKE_DOES_NOT_EXIST');
    };

    return (
        <>
            {instances.map((modal, index) => {
                const content: any = modal.content;
                return (
                    <Dialog key={modal.id ?? index} open={true} onOpenChange={() => { }}>
                        <DialogPortal>
                            <DialogContent
                                className={cn(
                                    'max-w-[min(90vw,var(--modal-max-width))] md:max-h-[92vh] max-h-[90vh] z-[1000]',
                                    modalVariants({ size: modal.size })
                                )}
                                onPointerDownOutside={() => {
                                    modal.closeWhenOutside && hideModal(modal.id);
                                }}
                            >
                                <DialogHeader
                                    className={cn(
                                        modal.title && 'xsm:-mx-6 -mx-4 pb-4 border-b border-border-2',
                                        modal.headerClassName,
                                        checkTypeModal(modal.type) && 'hidden'
                                    )}
                                >
                                    <DialogTitle
                                        className={cn(
                                            modalVariants({ type: modal.type }),
                                            modal.titleClassName
                                        )}
                                    >
                                        {modal.title}
                                    </DialogTitle>
                                    <DialogDescription>{modal.description}</DialogDescription>
                                    {modal.headerRender}
                                </DialogHeader>
                                {modal.showCloseIcon && (
                                    <DialogClose
                                        className='absolute -right-[1rem] -top-[1rem] bg-[#ddd] p-1.5 rounded-[50%] opacity-100 ring-offset-background transition-opacity hover:opacity-85 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground'
                                        onClick={() => {
                                            if (modal?.onClickXIcon) modal.onClickXIcon();
                                            hideModal(modal.id);
                                            if (checkRecordExist(content.props?.children?.i18nKey?.toString())) {
                                                window.location.href = isEk ? `${ROUTES.home.hrefEK}` : `${ROUTES.home.href}`;
                                            }
                                        }}
                                    >
                                        <X className='h-4 w-4' />
                                    </DialogClose>
                                )}
                                <div
                                    className={cn(
                                        'max-h-[calc(100vh-160px)] overflow-y-auto',
                                        checkTypeModal(modal?.type?.toString()) && 'text-center',
                                        modal.contentClassName
                                    )}
                                >
                                    {checkTypeModal(modal?.type?.toString()) && (
                                        <div className='flex justify-center mt-2 mb-5'>
                                            <img src={getImgType(modal?.type?.toString())} alt='' className='w-14' />
                                        </div>
                                    )}
                                    {isErrorModal(modal?.type?.toString()) ?
                                        (
                                            <>
                                                {content && typeof content?.props?.children === 'object' && content.props?.children?.i18nKey ?
                                                    (
                                                        <Trans i18nKey={content.props?.children?.i18nKey} values={content.props?.children?.values} />
                                                    ) :
                                                    (
                                                        content
                                                    )}
                                            </>
                                        ) :
                                        (
                                            <>
                                                {modal.content}
                                            </>
                                        )}
                                </div>
                                <DialogFooter
                                    className={cn(
                                        checkTypeModal(modal.type) && 'hidden',
                                        modal.footerClassName
                                    )}
                                >
                                    {modal.footer ?? (
                                        <ModalFooter
                                            cancelButton={modal.cancelButton}
                                            cancelButtonClassName={modal.cancelButtonClassName}
                                            onCancel={modal.onCancel}
                                            saveButton={modal.saveButton}
                                            saveButtonClassName={modal.saveButtonClassName}
                                            onSave={modal.onSave}
                                        />
                                    )}
                                </DialogFooter>
                            </DialogContent>
                        </DialogPortal>
                    </Dialog>
                );
            })}
        </>
    );
});
